import styles from "./index.module.scss";
import {
  MuteSvg,
  PauseSvg,
  PlaySvg,
  VolumeSvg,
} from "@/assets/icons";
import { CompareButton, ProgressBar, Tooltip } from "@/components";
import clsx from "clsx";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

export const formatSecond = (second: number) => {
  // 使用 Math.round 对秒数进行四舍五入
  second = Math.round(second);

  // 计算分钟和秒数
  const minutes: number = Math.floor(second / 60);
  const seconds: number = second % 60;

  // 将分钟和秒数格式化为 mm:ss
  const formattedMinutes: string =
    minutes < 10 ? "0" + minutes : String(minutes);
  const formattedSeconds: string =
    seconds < 10 ? "0" + seconds : String(seconds);

  // 返回格式化后的字符串
  return `${formattedMinutes}:${formattedSeconds}`;
};

export type PlayerControllerProps = {
  className?: string;
  style?: object;
  onPlay: () => void;
  onPause: () => void;
  onMute?: () => void;
  onCancelMute?: () => void;
  onChangeTime?: (value: number) => void;
  onCompare?: () => void;
  onCancelCompare?: () => void;
  onEnd?: () => void;
  isMobile?: boolean;
  isPlay: boolean;
  isMute?: boolean;
  duration: number;
  currentTime: number;
  prefix?: JSX.Element;
  suffix?: JSX.Element;
  disabled?: boolean;
  compareVisible?: boolean;
  getDuration?: () => number;
};

const PlayerController = (props: PlayerControllerProps) => {
  const {
    className,
    style = {},
    onPlay,
    onPause,
    onMute,
    onCancelMute,
    onChangeTime,
    onCompare,
    onCancelCompare,
    onEnd,
    isMobile = false,
    isPlay = false,
    isMute = false,
    duration = 0,
    currentTime = 0,
    prefix,
    suffix,
    disabled = false,
    compareVisible = false,
    getDuration,
  } = props;

  const { t } = useTranslation();
  const wasPlayRef = useRef(isPlay);

  const preventDefaultSpace = (e: any) => {
    if (e.code === "Space") {
      e.preventDefault();
    }
  };

  const handleControl = () => {
    wasPlayRef.current = isPlay;
    if (isPlay) {
      onPause?.();
    }
  }

  const handleOutOfControl = () => {
    if (wasPlayRef.current) {
      onPlay?.();
    }
  }
  
  return (
    <div 
      style={style}
      className={clsx(
        styles["player-controller"],
        className,
      )}
    >
      {prefix}

      {isPlay ? (
        <button
          disabled={disabled}
          onClick={onPause}
          onKeyDown={preventDefaultSpace}
        >
          <PauseSvg />

          <Tooltip
            content={t("player_pause_tip")}
          ></Tooltip>
        </button>
      ) : (
        <button
          disabled={disabled}
          onClick={onPlay}
          onKeyDown={preventDefaultSpace}
        >
          <PlaySvg />

          <Tooltip
            content={t("player_play_tip")}
          ></Tooltip>
        </button>
      )}

      {isMobile === false && (
        <>
          {isMute ? (
            <button
              disabled={disabled}
              onClick={onCancelMute}
              onKeyDown={preventDefaultSpace}
            >
              <MuteSvg />

              <Tooltip
                content={t("player_cancel_mute_tip")}
              ></Tooltip>
            </button>
          ) : (
            <button
              disabled={disabled}
              onClick={onMute}
              onKeyDown={preventDefaultSpace}
            >
              <VolumeSvg />

              <Tooltip
                content={t("player_mute_tip")}
              ></Tooltip>
            </button>
          )}
        </>
      )}

      <div className={styles["duration-wrapper"]}>
        <span className={styles["current-time"]}>
          {formatSecond(currentTime)}
        </span>
        <span className={styles["split-line"]}></span>
        <span className={styles["duration"]}>{formatSecond(duration)}</span>
      </div>

      {isMobile === false && (
        <div className={styles["progress-wrapper"]}>
          <ProgressBar
            isPlay={isPlay}
            getDuration={getDuration!}
            onChangeTime={onChangeTime}
            onOutOfControl={handleOutOfControl}
            onControl={handleControl}
            onEnd={onEnd}
          ></ProgressBar>
        </div>
      )}

      {compareVisible && (
        <CompareButton
          style={{ marginLeft: isMobile ? "auto" : 24 }}
          className={styles["custom_compare_button"]}
          onCompare={onCompare}
          onCancelCompare={onCancelCompare}
        >
          <Tooltip
            content={t("player_compare_tip")}
          ></Tooltip>
        </CompareButton>
      )}
      {suffix}
    </div>
  );
};

export default PlayerController;
